import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, X, ArrowRight } from "lucide-react";
import { getProviderForEdit } from "../../services/providersApi";
import "./ProviderCategoriesBox.css";

/**
 * Box que se muestra en el Home para proveedores de servicios.
 * Al hacer click abre un modal con las categorías del proveedor para que
 * elija de cuál quiere ver los posts publicados por usuarios.
 */
const ProviderCategoriesBox = ({ userData }) => {
    const navigate = useNavigate();
    const [categoryNames, setCategoryNames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (!userData?.providerRef) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const data = await getProviderForEdit(userData.providerRef);
                if (cancelled) return;
                const names = (data?.categoryNames && data.categoryNames.length > 0)
                    ? data.categoryNames
                    : (data?.categories || []);
                setCategoryNames(names.filter(Boolean));
            } catch (err) {
                console.warn("[ProviderCategoriesBox] no se pudieron cargar categorías:", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [userData?.providerRef]);

    const goToCategoryPosts = (categoryName) => {
        setIsModalOpen(false);
        navigate(`/servicios/${encodeURIComponent(categoryName)}/proveedores?view=posts`);
    };

    const handleOpen = () => {
        if (categoryNames.length === 0) return;
        if (categoryNames.length === 1) {
            goToCategoryPosts(categoryNames[0]);
            return;
        }
        setIsModalOpen(true);
    };

    const boxVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    };

    if (!loading && categoryNames.length === 0) return null;

    return (
        <>
            <motion.div
                className="home-box provider-categories-box"
                variants={boxVariants}
                initial="hidden"
                animate="visible"
                onClick={handleOpen}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpen();
                    }
                }}
            >
                <Layers className="title-icon provider-categories-box__icon" />
                <h2 className="home-title provider-categories-box__title">
                    <b>POSTS DE MIS CATEGORÍAS</b>
                </h2>
                <p className="provider-categories-box__subtitle">
                    <b>CONOCÉ LOS TRABAJOS DISPONIBLES</b>
                </p>
            </motion.div>

            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        className="pcb-modal-overlay"
                        onClick={() => setIsModalOpen(false)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="pcb-modal"
                            onClick={(e) => e.stopPropagation()}
                            initial={{ scale: 0.92, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.92, opacity: 0, y: 10 }}
                            transition={{ duration: 0.18 }}
                        >
                            <button
                                type="button"
                                className="pcb-modal__close"
                                onClick={() => setIsModalOpen(false)}
                                aria-label="Cerrar"
                            >
                                <X size={20} />
                            </button>
                            <h3 className="pcb-modal__title">Tus categorías</h3>
                            <p className="pcb-modal__subtitle">
                                Elegí una para ver los posts publicados por usuarios.
                            </p>
                            <ul className="pcb-modal__list">
                                {categoryNames.map((cat) => (
                                    <li key={cat}>
                                        <button
                                            type="button"
                                            className="pcb-modal__item"
                                            onClick={() => goToCategoryPosts(cat)}
                                        >
                                            <span className="pcb-modal__item-name">{cat}</span>
                                            <ArrowRight size={18} className="pcb-modal__item-arrow" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ProviderCategoriesBox;
